<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { ref, watch, computed } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { useHelpers, useTenantTextRules } from "@/helpers";
import { usePage } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";
import _ from "lodash";

const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const currentUser = usePage().props.value.auth.user;

const props = defineProps({
  allProjectManagers: Array,
  projectManagers: Array,
  filters: Object,
  sorting: Object,
  type: String,
  availableYears: Object,
  companies: Object,
  vendors: Object,
  colorField: Object,
});

console.log('Props:', props);

const projectNameRef = ref(null);
const companyNameRef = ref(null);
const vendorNameRef = ref(null);
const purchaseOrderRef = ref(null);
const trackingNumberRef = ref(null);

const defaultFilters = {
  project_manager: null,
  project_manager1: null,
  project_manager2: null,
  vendor: null,
  company: null,
  color: null,
  purchaseorder: null,
  projectname: null,
  tracking_number: null
}

watch(() => props.sorting, () => executeSearchRequest(), { deep: true })
const filterValues = ref(Object.assign({}, defaultFilters, props.filters));
const searching = ref(false);

const resetFilters = () => {
  searching.value = true;
  Inertia.get(route(route().current(), {}), {}, { preserveScroll: true });
  searching.value = false;
}

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['filters'] = filterValues.value;
  queryParameters['sorting'] = { column: props.sorting.column, direction: (props.sorting.is_ascending) ? 'asc' : 'desc' };

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish() {
      searching.value = false;
    }
  });
}

const selectedYears = computed(() => {
  return route().current().includes("closed")
    ? props.availableYears.closed
    : props.availableYears.created;
});

const companyOptions = computed(() =>
  (props.companies || [])
    .filter(c => c?.client && c?.ID != null)
    .map(c => ({ label: c.client, value: c.client }))
);

const vendorOptions = computed(() =>
  (props.vendors || [])
    .filter(v => v?.vendorName && v?.vendorID != null)
    .map(v => ({ label: v.vendorName, value: v.vendorName }))
);

const isStringOptionsColorField = computed(() => props.colorField?.mode === 'string_options');
const colorOptions = computed(() =>
  (props.colorField?.options || [])
    .map((option) => ({
      label: String(option?.label ?? ''),
      value: String(option?.value ?? ''),
    }))
    .filter(option => option.label && option.value)
);

const companyModel = computed({
  get: () => (filterValues.value.company ? String(filterValues.value.company) : null),
  set: (v) => { filterValues.value.company = v || null; },
});

const vendorModel = computed({
  get: () => (filterValues.value.vendor ? String(filterValues.value.vendor) : null),
  set: (v) => { filterValues.value.vendor = v || null; },
});

const colorModel = computed({
  get: () => {
    if (Array.isArray(filterValues.value.color)) {
      return filterValues.value.color[0] ? String(filterValues.value.color[0]) : null;
    }

    return filterValues.value.color ? String(filterValues.value.color) : null;
  },
  set: (v) => {
    filterValues.value.color = isStringOptionsColorField.value ? (v || null) : (v || null);
  },
});
const projectColorLabel = computed(() =>
  resolveTextRule('project.index.filter.color_label', 'Color', 'project.index.filter.color_label')
);
const projectColorPlaceholder = computed(() =>
  resolveTextRule('project.index.filter.color_placeholder', 'Color', 'project.index.filter.color_placeholder')
);
const purchaseOrderFilterLabel = computed(() =>
  resolveTextRule('project.index.filter.purchase_order', 'Purchase Order', 'project.index.filter.purchase_order')
);

</script>

<template>
  <div v-if="helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser)">
    <InputLabel for="company-name" value="Company Name" />
    <SearchSelect v-model="companyModel" :options="companyOptions" placeholder="Company Name" :disabled="searching"
      @change="makeSearchRequest" />
  </div>
  <div>
    <InputLabel for="project-name" value="Project Name" />
    <TextInput id="project-name" type="text" class="mt-1 w-full" placeholder="Project Name" ref="projectNameRef"
      v-model="filterValues.projectname" @keyup="makeSearchRequest" />
  </div>
  <div>
    <InputLabel for="project-color" :value="projectColorLabel" />
    <SearchSelect v-model="colorModel" :options="colorOptions" :placeholder="projectColorPlaceholder" :disabled="searching"
      @change="makeSearchRequest" />
  </div>
  <div>
    <InputLabel for="project-manager" value="Project Manager" />
    <SelectInput id="project-manager" class="mt-1"
      v-model="filterValues.project_manager" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="manager in projectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
    </SelectInput>
  </div>
  <div>
    <InputLabel for="project-manager-1" value="Secondary Project Manager 1" />
    <SelectInput id="project-manager-1" class="mt-1"
      v-model="filterValues.project_manager1" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="manager in allProjectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
    </SelectInput>
  </div>
  <div>
    <InputLabel for="project-manager-2" value="Secondary Project Manager 2" />
    <SelectInput id="project-manager-2" class="mt-1"
      v-model="filterValues.project_manager2" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="manager in allProjectManagers" :value="manager.employeeID" v-text="manager.full_name"></option>
    </SelectInput>
  </div>
  <div v-if="helpers.userCanManageProject(currentUser)">
    <InputLabel for="vendor-name" value="Vendor Name" />
    <SearchSelect v-model="vendorModel" :options="vendorOptions" placeholder="Vendor Name" :disabled="searching"
      @change="makeSearchRequest" />
  </div>
  <div v-if="route().current().includes('purchase-order')">
    <InputLabel for="purchase-order" :value="purchaseOrderFilterLabel" />
    <TextInput id="purchase-order" type="text" class="mt-1 w-full" :placeholder="purchaseOrderFilterLabel" ref="purchaseOrderRef"
      v-model="filterValues.purchaseorder" @keyup="makeSearchRequest" />
  </div>
  <div>
    <InputLabel for="year" value="Closed Year" v-if="route().current().includes('closed')" />
    <InputLabel for="year" value="Created Year" v-else />

    <SelectInput id="year" class="mt-1"
      v-model="filterValues.year" @change="makeSearchRequest" :disabled="searching">
      <option :value="null">Please select</option>
      <option v-for="year in selectedYears" :key="year" :value="year">{{ year }}</option>
    </SelectInput>
  </div>
  <div>
    <InputLabel for="tracking-number" value="Tracking Number" />
    <TextInput
      id="tracking-number"
      type="text"
      class="mt-1 w-full"
      placeholder="Tracking Number (e.g. 1Z..., LB..., etc.)"
      ref="trackingNumberRef"
      v-model="filterValues.tracking_number"
      @keyup="makeSearchRequest"
    />
  </div>
  <div>
    <InputLabel value="Reset Filter" class="text-gray-100" />
    <SecondaryButton @click="resetFilters" class="mt-1 h-[42px]">Reset Filters</SecondaryButton>
  </div>
</template>
